/**
 * AskRegistryBar — the "Ask the Registry" query bar for the Accountability Registry App Pack.
 *
 * One query the whole platform exists to answer: who owns what, right now — provably. Calls
 * GET /ownership/ask, which is deterministic by default and uses the platform's shared LLM router
 * when the ownership Ask-LLM flag is on (agentlets.ownership.ask.llm.enabled). Either way the
 * ANSWER is composed from live, RBAC-scoped registry state — never fabricated — so results are
 * auditable. Matching systems deep-link to the agent.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../../services/api';
import { Card, Pill, Button, Skeleton } from '../../components/v2';
import { Search, Sparkles, CornerDownLeft, X } from 'lucide-react';

// apiFetch wraps success as { ok, data, etag }; unwrap to the answer body.
const unwrap = (res) => (res && res.data !== undefined ? res.data : res);

function tierTone(tier) {
  switch (tier) {
    case 'TIER_1': return 'danger';
    case 'TIER_2': return 'warning';
    case 'TIER_3': return 'cyan';
    default: return 'muted';
  }
}

export default function AskRegistryBar() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sampleName, setSampleName] = useState('');

  // Seed the placeholder with a REAL agent name from the estate so the example works when typed.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const body = unwrap(await api.getAgents({}));
        const list = Array.isArray(body) ? body : (Array.isArray(body?.content) ? body.content : []);
        const named = list.find((a) => a && a.name);
        if (alive && named) setSampleName(named.name);
      } catch (e) { /* placeholder falls back to a generic hint */ }
    })();
    return () => { alive = false; };
  }, []);

  const ask = useCallback(async () => {
    const query = q.trim();
    if (!query) return;
    setLoading(true);
    setError(null);
    try {
      const body = unwrap(await api.askOwnershipRegistry(query));
      setAnswer(body || null);
    } catch (e) {
      setError(e?.message || 'Could not answer that just now.');
      setAnswer(null);
    } finally {
      setLoading(false);
    }
  }, [q]);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'Enter') { e.preventDefault(); ask(); }
  }, [ask]);

  const clear = useCallback(() => { setAnswer(null); setError(null); setQ(''); }, []);

  return (
    <div className="mb-4">
      <div className="relative max-w-[640px]">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-v2-ink-muted pointer-events-none" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={`Ask the Registry — try "who owns ${sampleName || '<agent name>'}?"`}
          aria-label="Ask the Registry"
          className="w-full pl-9 pr-24 py-2.5 rounded-v2-md border border-v2-border bg-v2-surface text-v2-ink text-[13px] placeholder:text-v2-ink-muted focus:outline-none focus:border-v2-action focus:shadow-v2-xs hover:border-v2-border-strong"
        />
        <div className="absolute right-1.5 top-1/2 -translate-y-1/2">
          <Button variant="primary" size="sm" loading={loading} iconLeft={<Sparkles size={14} />} onClick={ask}>
            Ask
          </Button>
        </div>
      </div>
      <div className="text-[11px] text-v2-ink-muted mt-1.5 flex items-center gap-1">
        <CornerDownLeft size={11} />
        One query the whole platform exists to answer: <i className="text-v2-ink-muted">who owns what, right now — provably.</i>
      </div>

      {loading ? (
        <Card padding="sm" className="mt-3 max-w-[720px]"><Skeleton variant="text" lines={2} /></Card>
      ) : error ? (
        <Card padding="sm" className="mt-3 max-w-[720px]">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[12.5px] text-v2-danger">{error}</span>
            <button onClick={clear} aria-label="Dismiss" className="text-v2-ink-muted hover:text-v2-ink"><X size={14} /></button>
          </div>
        </Card>
      ) : answer ? (
        <Card padding="sm" className="mt-3 max-w-[720px]">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[12px] text-v2-ink-muted">{answer.interpretation}</div>
              <div className="text-[13.5px] text-v2-ink font-medium mt-0.5">{answer.summary}</div>
            </div>
            <button onClick={clear} aria-label="Dismiss" className="text-v2-ink-muted hover:text-v2-ink flex-shrink-0"><X size={14} /></button>
          </div>
          {Array.isArray(answer.rows) && answer.rows.length > 0 ? (
            <div className="mt-2.5 divide-y divide-v2-border border-t border-v2-border">
              {answer.rows.map((r) => (
                <button
                  key={r.entryId || r.agentId}
                  onClick={() => r.agentId && navigate(`/agents/${r.agentId}`)}
                  className="w-full flex items-center gap-2 py-1.5 text-left hover:bg-v2-muted rounded px-1 -mx-1"
                >
                  <span className="text-[12.5px] text-v2-ink font-medium flex-1 min-w-0 truncate">{r.agentName || 'Unnamed agent'}</span>
                  {r.materialityTier ? <Pill tone={tierTone(r.materialityTier)} size="sm">{r.materialityTier.replace('TIER_', 'T')}</Pill> : null}
                  <span className="text-[12px] text-v2-ink-muted w-40 truncate text-right">
                    {r.ownerName || 'no accepted owner'}
                  </span>
                  <span className="text-[11px] text-v2-ink-muted w-24 truncate text-right">{r.ownershipStateLabel || r.ownershipState || ''}</span>
                </button>
              ))}
            </div>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}
